import React, { useMemo } from 'react';
import './ExecutionCommon.css';

function ExecutionMonitor({
  execution,
  processNodes,
  onNodeSelect,
  selectedNode,
  nodeResultsCache,
}) {
  // Get node status from execution state
  const getNodeStatus = (nodeId) => {
    if (!execution) return 'pending';
    if (execution.completed_nodes?.includes(nodeId)) return 'completed';
    if (execution.active_nodes?.includes(nodeId)) return 'active';
    if (execution.failed_nodes?.includes(nodeId)) return 'failed';
    if (execution.skipped_nodes?.includes(nodeId)) return 'skipped';
    return 'pending';
  };

  // Get node result from cache
  const getNodeResult = (nodeId) => {
    return nodeResultsCache?.[nodeId] || null;
  };

  // Sort nodes by execution order (completed first, then active, then pending)
  const sortedNodes = useMemo(() => {
    if (!processNodes) return [];
    
    return [...processNodes].sort((a, b) => {
      const statusOrder = {
        completed: 0,
        active: 1,
        failed: 2,
        skipped: 3,
        pending: 4,
      };
      const statusA = getNodeStatus(a.node_id);
      const statusB = getNodeStatus(b.node_id);
      return statusOrder[statusA] - statusOrder[statusB];
    });
  }, [processNodes, execution]);

  // Calculate elapsed time
  const getElapsedTime = () => {
    if (!execution?.started_at) return null;
    const start = new Date(execution.started_at);
    const end = execution.completed_at ? new Date(execution.completed_at) : new Date();
    const elapsed = Math.floor((end - start) / 1000);
    
    if (elapsed < 60) return `${elapsed}s`;
    if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
    return `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'active': return '●';
      case 'failed': return '✗';
      case 'skipped': return '○';
      default: return '○';
    }
  };

  // Get node type icon
  const getNodeTypeIcon = (node) => {
    if (node.execution_type === 'snn_pipeline') return '🧠';
    if (node.execution_type === 'llm') return '🤖';
    if (node.gate_type) return '◆';
    return '📦';
  };

  if (!execution) {
    return (
      <div className="execution-monitor empty">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Execution Running</h3>
          <p>Click "Run Process" to start a new execution</p>
        </div>
      </div>
    );
  }

  return (
    <div className="execution-monitor">
      {/* Summary Bar */}
      <div className="monitor-summary">
        <div className="summary-item">
          <span className="summary-label">Status</span>
          <span className={`summary-value status-${execution.status}`}>
            {execution.status?.toUpperCase()}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Progress</span>
          <span className="summary-value">{(execution.progress || 0).toFixed(1)}%</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Elapsed</span>
          <span className="summary-value">{getElapsedTime() || '-'}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Nodes</span>
          <span className="summary-value">
            {execution.completed_nodes?.length || 0}/{processNodes?.length || 0}
          </span>
        </div>
      </div>

      {/* Node Grid */}
      <div className="node-grid">
        <h4>Execution Flow</h4>
        <div className="nodes-container">
          {sortedNodes.map((node) => {
            const status = getNodeStatus(node.node_id);
            const result = getNodeResult(node.node_id);
            const isSelected = selectedNode?.node_id === node.node_id;

            return (
              <div
                key={node.node_id}
                className={`node-card ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => onNodeSelect(node)}
              >
                <div className="node-card-header">
                  <span className="node-type-icon">{getNodeTypeIcon(node)}</span>
                  <span className={`node-status-icon ${status}`}>
                    {getStatusIcon(status)}
                  </span>
                </div>
                <div className="node-card-body">
                  <span className="node-name">{node.name}</span>
                  <span className="node-type">{node.execution_type || 'node'}</span>
                </div>
                {result && (
                  <div className="node-card-footer">
                    {result.duration_ms && (
                      <span className="node-duration">{result.duration_ms}ms</span>
                    )}
                    {result.tokens_used > 0 && (
                      <span className="node-tokens">
                        {Number(result.tokens_used).toLocaleString()} tok
                      </span>
                    )}
                    {result.metadata?.rag_savings?.savings_pct > 0 && (
                      <span
                        className="node-tokens"
                        style={{color: '#047857', background: '#ecfdf5', borderColor: '#6ee7b7'}}
                        title={`Saved ${Number(result.metadata.rag_savings.savings_pct).toFixed(1)}% vs full-document RAG`}
                      >
                        -{Number(result.metadata.rag_savings.savings_pct).toFixed(0)}% RAG
                      </span>
                    )}
                  </div>
                )}
                {status === 'active' && (
                  <div className="node-spinner"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Gates Status */}
      {(execution.waiting_gates && Object.keys(execution.waiting_gates).length > 0) && (
        <div className="gates-status">
          <h4>Gates Status</h4>
          <div className="gates-list">
            {Object.entries(execution.waiting_gates).map(([gateId, inputs]) => (
              <div key={gateId} className="gate-item waiting">
                <span className="gate-icon">◇</span>
                <span className="gate-id">{gateId}</span>
                <span className="gate-inputs">{inputs.length} inputs satisfied</span>
              </div>
            ))}
            {execution.fired_gates?.map((gateId) => (
              <div key={gateId} className="gate-item fired">
                <span className="gate-icon">◆</span>
                <span className="gate-id">{gateId}</span>
                <span className="gate-status">SELECTED</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Nodes Detail */}
      {execution.active_nodes?.length > 0 && (
        <div className="active-nodes-section">
          <h4>Currently Executing</h4>
          <div className="active-nodes-list">
            {execution.active_nodes.map((nodeId) => {
              const node = processNodes.find(n => n.node_id === nodeId);
              return (
                <div key={nodeId} className="active-node-item">
                  <div className="active-indicator"></div>
                  <span className="active-node-name">{node?.name || nodeId}</span>
                  <span className="active-node-type">{node?.execution_type}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExecutionMonitor;
